import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { SearchInput } from '../input/input';

interface ContainerProps {
    columns: string[];
    children?: React.ReactNode;
}

const TableWrapper = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: visible;
`;

const Container = styled.div<ContainerProps>`
    display: grid;
    grid-template-columns: ${(props) => props.columns.join(' ')};
    grid-auto-rows: minmax(40px, auto);
`;

const Header = styled.div`
    display: contents;

    > * {
        &:first-child {
            padding-left: 12px;
        }
    }
`;

const HeaderCell = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 9px 12px;
    border-bottom: solid 1px #e5e7eb;
    background: #f9fafb;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 12px;
    color: #57534e;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    white-space: nowrap;

    &:first-child {
        border-top-left-radius: 8px;
    }

    &:last-child {
        border-top-right-radius: 8px;
    }

    .compact-table & {
        padding: 9px 12px;
    }
`;

export interface StyledRowProps {
    selected?: boolean;
    disabled?: boolean;
    className?: string;
}

const StyledRow = styled.div<StyledRowProps & { $clickable?: boolean }>`
    display: contents;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : props.$clickable ? 'pointer' : 'default')};
    ${(props) =>
        props.selected &&
        css`
            & > * {
                background-color: #f9fafb;
            }
        `}

    ${(props) =>
        props.disabled &&
        css`
            & > * {
                opacity: 0.5;
                pointer-events: none;
            }
        `}

    &:hover > * {
        background-color: #f9fafb;
    }

    > * {
        &:first-child {
            padding-left: 12px;
        }
        border-bottom: solid 1px #f3f4f6;
    }

    &:last-child > * {
        border-bottom: none;
    }
`;

export interface RowProps {
    cells: (React.ReactNode | [React.ReactNode, { isInput?: boolean }])[];
    onEnter?: () => void;
    onLeave?: () => void;
    onClick?: () => void;
}

const getCellComponent = (cell: React.ReactNode | [React.ReactNode, { isInput?: boolean }]) => {
    if (Array.isArray(cell)) return cell[0];
    return cell;
};

const getCellProperties = (cell: React.ReactNode | [React.ReactNode, { isInput?: boolean }]) => {
    if (Array.isArray(cell)) return cell[1];
    return { isInput: false };
};

export const Row: FC<RowProps & StyledRowProps> = ({ onClick, onEnter, onLeave, cells, className, disabled }) => {
    return (
        <StyledRow
            $clickable={!!onClick}
            onClick={disabled ? undefined : onClick}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            className={className}
            disabled={disabled}
        >
            {cells.map((cell, i) => (
                <Cell $isInput={getCellProperties(cell).isInput} key={i}>
                    {getCellComponent(cell)}
                </Cell>
            ))}
        </StyledRow>
    );
};

export const Cell = styled.span<{ $isInput?: boolean; $isPrintable?: boolean; $isHidden?: boolean }>`
    display: inline-flex;
    align-items: center;
    padding: ${(props) => (props.$isInput ? '8px 12px 8px 0px' : '8px 12px')};
    font-size: 13px;
    color: #263238;
    vertical-align: middle;

    .compact-table & {
        padding: ${(props) => (props.$isInput ? '6px 12px 6px 0px' : '6px 12px')};
        min-height: 56px;
        box-sizing: border-box;
    }
`;

export const ActionsCell = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 2px;
`;

export const LinkCell = styled.div`
    width: 100%;

    a {
        font-family: 'Inter', sans-serif;
        font-weight: 500;
        color: #263238;
        font-size: 13px;
        text-decoration: none;
        vertical-align: middle;

        &:hover {
            text-decoration: underline;
            cursor: pointer;
        }
    }
`;

const normalizeHeading = (heading: TableHeading): NormalizedTableHeading => {
    if (Array.isArray(heading)) {
        return heading;
    } else {
        return [heading, '1fr'];
    }
};

const headingSize = (heading: NormalizedTableHeading) => heading[1];
const headingName = (heading: NormalizedTableHeading) => heading[0];
type NormalizedTableHeading = [string | React.ReactElement, string];
export type TableHeading = string | NormalizedTableHeading;

interface TableProps {
    headings: TableHeading[];
    children?: React.ReactNode;
    isPrintable?: boolean;
    compact?: boolean;
    'data-testid'?: string;
}

export const Table: FC<TableProps> = ({ headings, children, compact, ...rest }) => {
    const normalizedHeadings = headings.map(normalizeHeading);

    return (
        <TableWrapper>
            <Container columns={normalizedHeadings.map(headingSize)} className={compact ? 'compact-table' : undefined} {...rest}>
                <Header>
                    {normalizedHeadings.map(headingName).map((name, i) => (
                        <HeaderCell key={i}>{name}</HeaderCell>
                    ))}
                </Header>

                {children}
            </Container>
        </TableWrapper>
    );
};

const TableFilter = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 4px;
    margin-bottom: 4px;
    flex-wrap: wrap;
`;

const LeftTableFilter = styled.div`
    display: flex;
    align-items: end;
    flex-direction: row;
    gap: 8px;
`;

const SearchInputContainer = styled.div`
    margin-left: auto;
    min-width: 300px;

    & > * {
        width: 100%;
        input {
            width: 100%;
            height: 28px;
            font-size: 12px;
            border: 1px solid #e5e7eb;
            background: #fff;
            border-radius: 6px;
            padding-left: 36px;

            &::placeholder {
                color: #78716c;
            }
        }
    }
`;

interface TableFilterHeaderProps {
    className?: string;
    showSearch?: boolean;
    onSearchChange?: (filter: string) => void;
    placeholder?: string;
    value?: string;
    autoFocus?: boolean;
    children?: React.ReactNode;
    customLeftComponent?: React.ReactNode;
    customRightComponent?: React.ReactNode;
}

export const TableFilterHeader: FC<TableFilterHeaderProps> = ({
    className,
    showSearch,
    onSearchChange,
    placeholder,
    value,
    autoFocus,
    children,
    customLeftComponent,
    customRightComponent
}) => {
    return (
        <TableFilter className={className}>
            {customLeftComponent}
            {children && <LeftTableFilter>{children}</LeftTableFilter>}
            {showSearch && (
                <SearchInputContainer className='search-input-container'>
                    <SearchInput autoFocus={autoFocus} placeholder={placeholder} value={value} onChange={(e) => onSearchChange!(e.currentTarget.value)} />
                </SearchInputContainer>
            )}
            {customRightComponent}
        </TableFilter>
    );
};
